// Module installation utilities
// Handles cloning templates, reports, and options sets from modules

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, updateDoc, doc, query, where, getDocs, DocumentReference, setDoc } from 'firebase/firestore';
import {
  cloneTemplateFromModule,
  cloneReportFromModule,
  cloneOptionsSetFromModule,
  generateUniqueId,
  TemplateField
} from './module-cloning-utils';
import { fetchModulesFromFirestore, FlexModule } from './modules';

export interface SavedComponent {
  id: string;
  name: string;
  originalId: string;
  linkedFormTemplateId?: string;
  originalOptionsSetId?: string;
}

export type ModuleInstallMode = 'clean' | 'sample';

export interface InstallModuleOptions {
  mode?: ModuleInstallMode;
}

export type InstallOptions = InstallModuleOptions;

export interface InstallResult {
  templates: SavedComponent[];
  reports: SavedComponent[];
  sampleOptionsSets: SavedComponent[];
  sampleSubmissionsCreated?: number;
  instructions?: string;
  nextSteps?: string[];
}

export interface UpdateResult {
  templatesUpdated: SavedComponent[];
  reportsUpdated: SavedComponent[];
  optionsSetsUpdated: SavedComponent[];
}

/**
 * Install a module by cloning all its components and saving them to Firestore
 */
export const installModule = async (
  module: FlexModule,
  companyId: string,
  userEmail: string,
  options: InstallModuleOptions = { mode: 'clean' }
): Promise<InstallResult> => {
  const mode: ModuleInstallMode = options?.mode || 'clean';

  const results: InstallResult = {
    templates: [],
    reports: [],
    sampleOptionsSets: [],
    sampleSubmissionsCreated: 0,
    instructions: module.instructions,
    nextSteps: module.nextSteps
  };

  // Clone and save sample options sets first (needed for template optionsSource mapping)
  const sampleOptionsMapping = new Map<string, string>(); // Map original sample ID to new Firestore ID
  const optionsSetsToInstall = module.optionsSets || module.sampleOptionsSets;
  if (optionsSetsToInstall) {
    for (const sampleOptionsConfig of optionsSetsToInstall) {
      const rawOptions = sampleOptionsConfig.options || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).options) || [];
      const linkedFormTemplateId = sampleOptionsConfig.linkedFormTemplateId || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).linkedFormTemplateId);
      const mappingConfig = sampleOptionsConfig.mappingConfig || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).mappingConfig);

      // Determine if option set is dynamically linked to a template or mapping
      const isDynamicallyLinked = Boolean(linkedFormTemplateId || mappingConfig);
      // Clean mode produces empty options for dynamically linked sets; static sets retain options
      const optionsToUse = (mode === 'clean' && isDynamicallyLinked) ? [] : rawOptions;

      const clonedOptions = cloneOptionsSetFromModule({
        id: sampleOptionsConfig.id,
        name: sampleOptionsConfig.name,
        options: optionsToUse,
        description: sampleOptionsConfig.description || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).metadata && (sampleOptionsConfig.placeholderData as any).metadata.description),
        linkedFormTemplateId,
        mappingConfig
      }, module, companyId, userEmail);

      // Resolve name collisions by appending (1), (2), etc.
      let finalName: string = sampleOptionsConfig.name;
      let suffix = 1;
      while (true) {
        const qExisting = query(
          collection(db, 'flexFormSelectOptions'),
          where('companyId', '==', companyId),
          where('name', '==', finalName)
        );
        const existingSnapshot = await getDocs(qExisting);
        if (existingSnapshot.empty) {
          break;
        }
        finalName = `${sampleOptionsConfig.name} (${suffix})`;
        suffix++;
      }

      clonedOptions.name = finalName;

      // Save new options set to Firestore
      const optionsDoc = {
        ...clonedOptions,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'flexFormSelectOptions'), optionsDoc);
      const docId = docRef.id;
      // Update the document to set the "id" field to the Firestore document ID
      await updateDoc(docRef, { id: docId });

      // Store mapping for template updates
      sampleOptionsMapping.set(sampleOptionsConfig.id, docId);

      const savedOptions: SavedComponent = {
        id: docId,
        name: clonedOptions.name,
        originalId: clonedOptions.metadata.originalModuleId,
        originalOptionsSetId: sampleOptionsConfig.id,
        linkedFormTemplateId: clonedOptions.linkedFormTemplateId
      };
      results.sampleOptionsSets.push(savedOptions);
    }
  }

  // Clone and save templates
  for (const templateConfig of module.templates) {
    const templateData = templateConfig.templateData;
    const clonedTemplate = cloneTemplateFromModule(templateData as any, module, companyId, userEmail, sampleOptionsMapping);

    // Save template to Firestore
    const templateDoc = {
      ...clonedTemplate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'flexFormTemplates'), templateDoc);

    // Update the document to set the "id" field to the Firestore document ID
    await updateDoc(docRef, { id: docRef.id });

    // Save AI story template if defined
    if (templateConfig.storyTemplate) {
      const storyDoc = {
        templateId: docRef.id,
        companyId: companyId,
        handlebarsTemplate: templateConfig.storyTemplate.handlebarsTemplate,
        active: templateConfig.storyTemplate.active,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(doc(db, 'flexFormStoryTemplates', docRef.id), storyDoc);
    }

    const savedTemplate: SavedComponent = {
      id: docRef.id,
      name: clonedTemplate.name,
      originalId: clonedTemplate.moduleMetadata.originalTemplateId
    };
    results.templates.push(savedTemplate);
  }

  // Resolve and update linkedFormTemplateId to the newly created Firestore template IDs
  for (const savedOptions of results.sampleOptionsSets) {
    if (savedOptions.linkedFormTemplateId) {
      const matchingTemplate = results.templates.find(t => t.originalId === savedOptions.linkedFormTemplateId);
      if (matchingTemplate) {
        await updateDoc(doc(db, 'flexFormSelectOptions', savedOptions.id), {
          linkedFormTemplateId: matchingTemplate.id,
          updatedAt: serverTimestamp()
        });
        savedOptions.linkedFormTemplateId = matchingTemplate.id;
      }
    }
  }

  // Clone and save reports
  for (const reportConfig of module.reports) {
    const reportData = reportConfig.reportData;
    if (reportData) {
      const clonedReport = cloneReportFromModule(reportData, module, companyId, userEmail);

      // Update template references to use new template IDs
      clonedReport.includedTemplates = clonedReport.includedTemplates.map((templateId: string) => {
        const matchingTemplate = results.templates.find(t =>
          t.originalId === templateId
        );
        return matchingTemplate ? matchingTemplate.id : templateId;
      });

      // Replace placeholders in the SQL query statement
      // E.g. replace {{tpl_clock_in}} with the new Firestore ID
      let selectStatement = clonedReport.reportSelectStatement;
      if (selectStatement) {
        // Replace Template IDs
        results.templates.forEach(t => {
          const regex = new RegExp(`\\{\\{${t.originalId}\\}\\}`, 'g');
          selectStatement = selectStatement.replace(regex, t.id);
        });
        
        // Replace Options Set IDs
        results.sampleOptionsSets.forEach(o => {
          const originalId = o.originalOptionsSetId || o.originalId;
          const regex = new RegExp(`\\{\\{${originalId}\\}\\}`, 'g');
          selectStatement = selectStatement.replace(regex, o.id);
        });

        clonedReport.reportSelectStatement = selectStatement;
      }

      // Save report to Firestore
      const reportDoc = {
        ...clonedReport,
      };
      let docRef: DocumentReference | null = null;
      try {
        docRef = await addDoc(collection(db, 'flexReports'), reportDoc);
        // Update the document to set the "id" field to the Firestore document ID
        await updateDoc(docRef, { id: docRef.id });
      } catch (error) {
        console.error("Error saving cloned report to Firestore:", error);
      }

      if (docRef) {
        const savedReport: SavedComponent = {
          id: docRef.id,
          name: clonedReport.name,
          originalId: clonedReport.moduleMetadata.originalReportId
        };
        results.reports.push(savedReport);
      }
    }
  }

  // If in sample mode, seed sample submissions into flexFormSubmissions
  if (mode === 'sample') {
    let submissionsCreated = 0;
    const seededTemplateIds = new Set<string>();

    // 1. Seed from module.sampleSubmissions or module.mockData if available
    const explicitSubmissions: any[] = (module as any).sampleSubmissions || (module as any).mockData || [];
    for (let i = 0; i < explicitSubmissions.length; i++) {
      const subItem = explicitSubmissions[i];
      const origTemplateId = subItem.templateId;
      const matchingTemplate = results.templates.find(
        t => t.originalId === origTemplateId || t.id === origTemplateId
      );

      if (matchingTemplate) {
        const subData = subItem.data || {};
        const structuredData: Record<string, any> = { ...subData };
        if (!subItem.data) {
          const systemKeys = new Set([
            'documentNumber', 'templateId', 'templateName', 'submitterName',
            'submitterEmail', 'submissionDate', 'companyId', 'isSampleData',
            'id', 'createdAt', 'updatedAt'
          ]);
          for (const [k, v] of Object.entries(subItem)) {
            if (!systemKeys.has(k) && !k.endsWith('_value') && !k.endsWith('_label')) {
              structuredData[k] = (typeof v === 'object' && v !== null && 'value' in v)
                ? v
                : { type: 'text', value: v };
            }
          }
        }

        const subDoc = {
          ...subItem,
          templateId: matchingTemplate.id,
          templateName: matchingTemplate.name || subItem.templateName || "Form Submission",
          companyId,
          documentNumber: subItem.documentNumber || `SAMPLE-${matchingTemplate.name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}-${String(i + 1).padStart(3, '0')}`,
          submitterName: subItem.submitterName || "Sample Data",
          submitterEmail: subItem.submitterEmail || userEmail || "sample@captiva.local",
          submissionDate: subItem.submissionDate ? new Date(subItem.submissionDate) : new Date(),
          isSampleData: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          data: structuredData,
        };

        const subDocRef = await addDoc(collection(db, 'flexFormSubmissions'), subDoc);
        await updateDoc(subDocRef, { id: subDocRef.id });
        submissionsCreated++;
        seededTemplateIds.add(matchingTemplate.id);
      }
    }

    // 2. Synthesize matching submissions for dynamically linked option sets with sample options
    if (optionsSetsToInstall) {
      for (const sampleOptionsConfig of optionsSetsToInstall) {
        const rawOptions = sampleOptionsConfig.options || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).options) || [];
        const originalLinkedTplId = sampleOptionsConfig.linkedFormTemplateId || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).linkedFormTemplateId);
        const mappingConfig = sampleOptionsConfig.mappingConfig || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).mappingConfig);

        if (originalLinkedTplId && rawOptions.length > 0) {
          const matchingTemplate = results.templates.find(t => t.originalId === originalLinkedTplId);
          if (matchingTemplate && !seededTemplateIds.has(matchingTemplate.id)) {
            // Seed a matching submission for each sample option so True Sync can reconstruct it
            for (let optIdx = 0; optIdx < rawOptions.length; optIdx++) {
              const opt = rawOptions[optIdx];
              const context: Record<string, any> = {
                id: opt.value,
                value: opt.value,
                label: opt.label,
              };

              const extractTokens = (str?: string) => {
                if (!str) return [];
                const m = str.match(/\{\{([^}]+)\}\}/g);
                return m ? m.map(s => s.replace(/[{}]/g, '').trim()) : [];
              };

              const valTokens = extractTokens(mappingConfig?.valueTemplate);
              if (valTokens.length === 1) {
                context[valTokens[0]] = opt.value;
              }

              const labelTokens = extractTokens(mappingConfig?.labelTemplate);
              if (labelTokens.length === 1) {
                context[labelTokens[0]] = opt.label;
              } else if (labelTokens.length === 2) {
                const words = String(opt.label || "").trim().split(/\s+/);
                context[labelTokens[0]] = words[0] || opt.label;
                context[labelTokens[1]] = words.slice(1).join(" ") || "";
              } else if (labelTokens.length > 2) {
                const words = String(opt.label || "").trim().split(/\s+/);
                labelTokens.forEach((tok, idx) => {
                  context[tok] = words[idx] || "";
                });
              }

              if (mappingConfig?.metadata && typeof mappingConfig.metadata === 'object') {
                Object.entries(mappingConfig.metadata).forEach(([metaKey, metaConf]: [string, any]) => {
                  const mTokens = extractTokens(metaConf?.template);
                  const metaVal = opt.metadata?.[metaKey]?.value ?? opt.metadata?.[metaKey] ?? "";
                  if (mTokens.length > 0) {
                    mTokens.forEach(t => { context[t] = metaVal; });
                  } else {
                    context[metaKey] = metaVal;
                  }
                });
              }

              if (opt.metadata && typeof opt.metadata === 'object') {
                Object.entries(opt.metadata).forEach(([k, v]: [string, any]) => {
                  const val = (v && typeof v === 'object' && 'value' in v) ? v.value : v;
                  if (context[k] === undefined) {
                    context[k] = val;
                  }
                });
              }

              const structuredData: Record<string, any> = {};
              Object.entries(context).forEach(([k, v]) => {
                structuredData[k] = { type: 'text', value: v };
              });

              const optDocNum = `SAMPLE-${(sampleOptionsConfig.name || "OPT").replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}-${String(optIdx + 1).padStart(3, '0')}`;

              const subDoc = {
                templateId: matchingTemplate.id,
                templateName: matchingTemplate.name || "Form Submission",
                companyId,
                documentNumber: optDocNum,
                submitterName: "Sample Data",
                submitterEmail: userEmail || "sample@captiva.local",
                submissionDate: new Date(),
                isSampleData: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                data: structuredData,
                ...context
              };

              const subDocRef = await addDoc(collection(db, 'flexFormSubmissions'), subDoc);
              await updateDoc(subDocRef, { id: subDocRef.id });
              submissionsCreated++;
            }
            seededTemplateIds.add(matchingTemplate.id);
          }
        }
      }
    }

    results.sampleSubmissionsCreated = submissionsCreated;
  }

  return results;
};

// API Error class
export class ModuleApiError extends Error {
  statusCode: number;
  errorCode: string;

  constructor(message: string, statusCode: number, errorCode: string) {
    super(message);
    this.name = 'ModuleApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

/**
 * Get list of available modules
 */
export const getModules = async (companyId: string, options: any = {}): Promise<any> => {
  try {
    // Fetch modules from Firestore
    const modules = await fetchModulesFromFirestore();

    // Transform to the expected format
    const transformedModules = modules.map(module => ({
      id: module.id,
      name: module.name,
      description: module.description,
      category: 'Operations', // Default category
      version: module.version || '1.0.0',
      isInstalled: false, // This would need to be checked against installation records
      thumbnailUrl: null
    }));

    return {
      modules: transformedModules,
      total: transformedModules.length,
      hasMore: false
    };
  } catch (error) {
    throw new ModuleApiError('Failed to fetch modules', 500, 'MODULES_FETCH_FAILED');
  }
};

/**
 * Install a module for a company
 */
export const installModuleApi = async (
  moduleId: string,
  companyId: string,
  userId: string,
  userEmail: string,
  options: InstallModuleOptions = { mode: 'clean' }
): Promise<any> => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Fetch modules from Firestore
    const modules = await fetchModulesFromFirestore();
    const moduleData = modules.find(m => m.id === moduleId);
    if (!moduleData) {
      throw new ModuleApiError('Module not found', 404, 'MODULE_NOT_FOUND');
    }

    // Check if already installed (mock)
    const isAlreadyInstalled = false; // Mock
    if (isAlreadyInstalled) {
      throw new ModuleApiError('Module already installed', 409, 'MODULE_ALREADY_INSTALLED');
    }

    // Use our module installer to generate and save cloned components
    const installResult = await installModule(moduleData, companyId, userEmail, options);

    return {
      success: true,
      installedComponents: {
        templates: installResult.templates,
        reports: installResult.reports,
        sampleOptionsSets: installResult.sampleOptionsSets
      },
      sampleSubmissionsCreated: installResult.sampleSubmissionsCreated || 0,
      instructions: installResult.instructions,
      nextSteps: installResult.nextSteps,
      errors: []
    };

  } catch (error) {
    if (error instanceof ModuleApiError) {
      throw error;
    }
    throw new ModuleApiError('Module installation failed', 500, 'MODULE_INSTALL_FAILED');
  }
};

/**
 * Get detailed information about a specific module
 */
export const getModuleDetails = async (moduleId: string, companyId: string): Promise<any> => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 150));

    // Fetch modules from Firestore
    const modules = await fetchModulesFromFirestore();
    const moduleData = modules.find(m => m.id === moduleId);
    if (!moduleData) {
      throw new ModuleApiError('Module not found', 404, 'MODULE_NOT_FOUND');
    }

    return {
      module: moduleData,
      isInstalled: false, // Mock: not installed
      installedComponents: null
    };
  } catch (error) {
    if (error instanceof ModuleApiError) {
      throw error;
    }
    throw new ModuleApiError('Failed to fetch module details', 500, 'MODULE_DETAILS_FETCH_FAILED');
  }
};

/**
 * Update an existing module installation by merging/updating cloned components in-place
 */
export const updateModule = async (module: FlexModule, companyId: string, userEmail: string): Promise<UpdateResult> => {
  const results: UpdateResult = {
    templatesUpdated: [],
    reportsUpdated: [],
    optionsSetsUpdated: [],
  };

  // 1. Fetch all existing cloned templates, reports, and options sets for this module
  const [templatesSnap, reportsSnap, optionsSnap] = await Promise.all([
    getDocs(query(collection(db, 'flexFormTemplates'), where('companyId', '==', companyId), where('moduleId', '==', module.id))),
    getDocs(query(collection(db, 'flexReports'), where('companyId', '==', companyId), where('moduleId', '==', module.id))),
    getDocs(query(collection(db, 'flexFormSelectOptions'), where('companyId', '==', companyId), where('moduleId', '==', module.id)))
  ]);

  const existingTemplates = templatesSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));
  const existingReports = reportsSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));
  const existingOptionsSets = optionsSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));

  // Build sample options mapping from existing options sets
  const sampleOptionsMapping = new Map<string, string>();
  const optionsSetsToInstall = module.optionsSets || module.sampleOptionsSets || [];
  for (const sampleOptionsConfig of optionsSetsToInstall) {
    const existingOpt = existingOptionsSets.find((o: any) =>
      o.metadata?.originalOptionsSetId === sampleOptionsConfig.id ||
      o.name === sampleOptionsConfig.name ||
      o.name?.startsWith(sampleOptionsConfig.name)
    );
    if (existingOpt) {
      sampleOptionsMapping.set(sampleOptionsConfig.id, existingOpt.id);
    }
  }

  // Map originalTemplateId to current Firestore template ID
  const templateMapping = new Map<string, string>();
  existingTemplates.forEach((t: any) => {
    if (t.moduleMetadata?.originalTemplateId) {
      templateMapping.set(t.moduleMetadata.originalTemplateId, t.id);
    }
  });

  // 2. Update existing templates in-place, or install new templates from the module
  for (const templateConfig of module.templates || []) {
    const templateData = templateConfig.templateData;
    if (!templateData) continue;

    const existingTpl = existingTemplates.find((t: any) =>
      t.moduleMetadata?.originalTemplateId === templateConfig.id ||
      t.name === templateConfig.name
    );

    if (existingTpl) {
      const existingFields: any[] = existingTpl.fields || [];
      const fieldIdMapping = new Map<string, string>(); // Map module field ID to company field ID

      // Map existing field keys to their current company field IDs
      const existingKeyToId = new Map<string, string>();
      existingFields.forEach(f => {
        if (f.key) existingKeyToId.set(f.key, f.id);
      });

      // Map module fields, preserving existing company field ID if keys match
      const updatedFields = (templateData.fields || []).map((field: TemplateField) => {
        let fieldId = existingKeyToId.get(field.key);
        if (!fieldId) {
          fieldId = generateUniqueId();
        }
        fieldIdMapping.set(field.id, fieldId);

        const updatedField: any = { ...field, id: fieldId };

        // Handle sampleOptionsId mapping
        if (field.sampleOptionsId && sampleOptionsMapping.has(field.sampleOptionsId)) {
          const firestoreId = sampleOptionsMapping.get(field.sampleOptionsId)!;
          updatedField.optionsSource = {
            type: 'firestore',
            collection: 'flexFormSelectOptions',
            document: firestoreId,
            id: firestoreId
          };
          delete updatedField.sampleOptionsId;
        }

        return updatedField;
      });

      // Remap internal references (displayIf and dependencies)
      const mappedFields = updatedFields.map(field => {
        let mappedField = { ...field };

        if (Array.isArray(mappedField.displayIf)) {
          mappedField.displayIf = mappedField.displayIf.map((cond: any) => ({
            ...cond,
            fieldId: fieldIdMapping.get(cond.fieldId) || cond.fieldId
          }));
        }

        if (Array.isArray(mappedField.dependencies)) {
          mappedField.dependencies = mappedField.dependencies.map((dep: any) => ({
            ...dep,
            fieldId: fieldIdMapping.get(dep.fieldId) || dep.fieldId
          }));
        }

        return mappedField;
      });

      // Remap section field references
      const updatedSections = templateData.sections ? templateData.sections.map(section => ({
        ...section,
        fields: section.fields.map(fieldId => fieldIdMapping.get(fieldId) || fieldId)
      })) : (existingTpl.sections || []);

      const templateUpdateData: any = {
        name: templateConfig.name,
        description: templateData.description || existingTpl.description || "",
        fields: mappedFields,
        sections: updatedSections,
        updatedBy: userEmail || "module-update",
        updatedAt: serverTimestamp(),
        moduleMetadata: {
          ...(existingTpl.moduleMetadata || {}),
          originalModuleId: module.id,
          originalModuleName: module.name,
          originalTemplateId: templateConfig.id,
          version: module.version || "1.0.0"
        }
      };

      if (templateData.notificationSubjectLine) {
        templateUpdateData.notificationSubjectLine = templateData.notificationSubjectLine;
      }
      if (templateData.notificationBody) {
        templateUpdateData.notificationBody = templateData.notificationBody;
      }

      await updateDoc(doc(db, 'flexFormTemplates', existingTpl.id), templateUpdateData);
      templateMapping.set(templateConfig.id, existingTpl.id);
      results.templatesUpdated.push({
        id: existingTpl.id,
        name: templateConfig.name,
        originalId: templateConfig.id
      });
    } else {
      // New template added in this module version - install fresh
      const clonedTemplate = cloneTemplateFromModule(templateData as any, module, companyId, userEmail, sampleOptionsMapping);
      const templateDoc = {
        ...clonedTemplate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'flexFormTemplates'), templateDoc);
      templateMapping.set(templateConfig.id, docRef.id);
      results.templatesUpdated.push({
        id: docRef.id,
        name: templateConfig.name,
        originalId: templateConfig.id
      });
    }
  }

  // 3. Update each options set in-place
  for (const sampleOptionsConfig of optionsSetsToInstall) {
    const existingOpt = existingOptionsSets.find((o: any) =>
      o.metadata?.originalOptionsSetId === sampleOptionsConfig.id ||
      o.name === sampleOptionsConfig.name ||
      o.name?.startsWith(sampleOptionsConfig.name)
    );
    if (existingOpt) {
      const linkedTemplateId = sampleOptionsConfig.linkedFormTemplateId || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).linkedFormTemplateId);
      const mappedTemplateId = linkedTemplateId ? templateMapping.get(linkedTemplateId) : null;

      const updatedFields = {
        mappingConfig: sampleOptionsConfig.mappingConfig || (sampleOptionsConfig.placeholderData && (sampleOptionsConfig.placeholderData as any).mappingConfig) || null,
        ...(mappedTemplateId && { linkedFormTemplateId: mappedTemplateId }),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'flexFormSelectOptions', existingOpt.id), updatedFields);
      results.optionsSetsUpdated.push({ id: existingOpt.id, name: existingOpt.name, originalId: sampleOptionsConfig.id });
    }
  }

  // 4. Update each report in-place
  const reportsToInstall = module.reports || [];
  for (const reportConfig of reportsToInstall) {
    const reportData = reportConfig.reportData;
    if (!reportData) continue;

    const existingRep = existingReports.find((r: any) => r.moduleMetadata?.originalReportId === reportData.id);
    if (existingRep) {
      let selectStatement = reportData.reportSelectStatement;
      if (selectStatement) {
        // Replace Template IDs based on template mapping
        templateMapping.forEach((newId, originalId) => {
          const regex = new RegExp(`\\{\\{${originalId}\\}\\}`, 'g');
          selectStatement = selectStatement.replace(regex, newId);
        });
        
        // Replace Options Set IDs based on existing options sets
        existingOptionsSets.forEach((o: any) => {
          const originalId = o.metadata?.originalOptionsSetId || o.originalOptionsSetId;
          if (originalId) {
            const regex = new RegExp(`\\{\\{${originalId}\\}\\}`, 'g');
            selectStatement = selectStatement.replace(regex, o.id);
          }
        });
      }

      const updatedFields = {
        name: reportData.name,
        description: reportData.description || "",
        dataSource: reportData.dataSource,
        reportSelectStatement: selectStatement,
        reportTemplateLink: reportData.reportTemplateLink,
        renderingType: reportData.renderingType,
        reportShortName: reportData.reportShortName || reportData.name || "",
        isActive: reportData.isActive ?? true,
        moduleName: reportData.moduleName || module.name || "",
        updatedBy: userEmail || "module-update",
        updatedAt: serverTimestamp(),
        includedTemplates: (reportData.includedTemplates || []).map((tId: string) => templateMapping.get(tId) || tId),
        ...(reportData.postProcessingType && { postProcessingType: reportData.postProcessingType }),
        ...(reportData.postProcessingParams && { postProcessingParams: reportData.postProcessingParams }),
        ...(reportData.runtimeFilters && { runtimeFilters: reportData.runtimeFilters }),
      };

      await updateDoc(doc(db, 'flexReports', existingRep.id), updatedFields);
      results.reportsUpdated.push({ id: existingRep.id, name: reportData.name, originalId: reportData.id });
    }
  }

  // 5. Update each template's story template in-place
  for (const templateConfig of module.templates || []) {
    const templateId = templateConfig.id;
    const mappedTemplateId = templateMapping.get(templateId);
    if (mappedTemplateId && templateConfig.storyTemplate) {
      const storyDoc = {
        templateId: mappedTemplateId,
        companyId: companyId,
        handlebarsTemplate: templateConfig.storyTemplate.handlebarsTemplate,
        active: templateConfig.storyTemplate.active,
        updatedAt: serverTimestamp()
      };
      await setDoc(doc(db, 'flexFormStoryTemplates', mappedTemplateId), storyDoc, { merge: true });
    }
  }

  return results;
};

/**
 * API wrapper for updating modules
 */
export const updateModuleApi = async (moduleId: string, companyId: string, userId: string, userEmail: string): Promise<any> => {
  try {
    const modules = await fetchModulesFromFirestore();
    const moduleData = modules.find(m => m.id === moduleId);
    if (!moduleData) {
      throw new ModuleApiError('Module not found', 404, 'MODULE_NOT_FOUND');
    }

    const updateResult = await updateModule(moduleData, companyId, userEmail);

    return {
      success: true,
      updatedComponents: {
        templates: updateResult.templatesUpdated,
        reports: updateResult.reportsUpdated,
        optionsSets: updateResult.optionsSetsUpdated
      }
    };
  } catch (error) {
    if (error instanceof ModuleApiError) {
      throw error;
    }
    throw new ModuleApiError('Module update failed', 500, 'MODULE_UPDATE_FAILED');
  }
};